import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { logService } from '../lib/logService';
import { DEFAULT_MARKETING_SETTINGS } from '../types/forms';
import type { MarketingSettings } from '../types/forms';

// Platform-level marketing (no id, user_id, timestamps)
export type PlatformMarketing = Omit<MarketingSettings, 'id' | 'user_id' | 'created_at' | 'updated_at'>;

// Public hook — reads marketing config for script injection
export function useMarketingConfig() {
  const [config, setConfig] = useState<PlatformMarketing>(DEFAULT_MARKETING_SETTINGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const { data, error } = await supabase.rpc('get_platform_setting', { p_key: 'marketing' });
        if (error) {
          logService.error('marketing.fetch', error.message, { supabase_code: error.code });
        } else if (data) {
          setConfig(data as PlatformMarketing);
        }
      } catch (err) {
        logService.error('marketing.fetch', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  return { config, loading };
}

// Admin hook — reads + writes marketing config
export function useAdminMarketingSettings() {
  const [config, setConfig] = useState<PlatformMarketing>(DEFAULT_MARKETING_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const { data, error } = await supabase.rpc('get_platform_setting', { p_key: 'marketing' });
        if (error) {
          logService.error('marketing.admin_fetch', error.message, { supabase_code: error.code });
        } else if (data) {
          setConfig(data as PlatformMarketing);
        }
      } catch (err) {
        logService.error('marketing.admin_fetch', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const save = async (newConfig: PlatformMarketing) => {
    setSaving(true);
    setError(null);
    let resultError: string | null = null;
    try {
      const { error: e } = await supabase.rpc('admin_set_platform_setting', {
        p_key: 'marketing',
        p_value: newConfig as unknown as Record<string, unknown>,
      });
      if (e) {
        logService.error('marketing.admin_save', e.message, { supabase_code: e.code });
        resultError = e.message;
        setError(e.message);
      } else {
        setConfig(newConfig);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      logService.error('marketing.admin_save', msg, { error: err as Error });
      resultError = 'Erro inesperado ao salvar configurações';
      setError(resultError);
    } finally {
      setSaving(false);
    }
    return { error: resultError };
  };

  return { config, loading, saving, error, save, setConfig };
}
